import { useState } from 'react'
// @mui
import Button from '@mui/material/Button'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
// hooks
import { useBoolean } from 'src/hooks/use-boolean'
// types
import { IUserItem } from 'src/types/user'
// components
import Label from 'src/components/label'
import Iconify from 'src/components/iconify'
import { ConfirmDialog } from 'src/components/custom-dialog'
import { format } from 'date-fns'
import { masks } from 'src/utils/masks'
import UserCreateEditForm from '../user-create-edit-view'

// ----------------------------------------------------------------------

const ROLE_CONFIG: Record<string, { label: string; color: 'primary' | 'warning' | 'default' }> = {
  admin: { label: 'Admin', color: 'primary' },
  franchisee: { label: 'Franqueado', color: 'warning' },
  user: { label: 'Vendedor', color: 'default' },
}

const PAYMENT_ICONS: { key: keyof NonNullable<IUserItem['allowedPayments']>; icon: string; label: string; color: string }[] = [
  { key: 'money', icon: 'solar:banknote-bold', label: 'Dinheiro', color: '#16a34a' },
  { key: 'pix', icon: 'solar:qr-code-bold', label: 'Pix', color: '#0891b2' },
  { key: 'credit', icon: 'solar:card-bold', label: 'Crédito', color: '#dc2626' },
  { key: 'debit', icon: 'solar:card-2-bold', label: 'Débito', color: '#2563eb' },
  { key: 'cashless', icon: 'solar:card-send-bold', label: 'CashLess', color: '#7c3aed' },
]

type Props = {
  selected: boolean
  row: IUserItem
  refetch: VoidFunction
  onDeleteRow: VoidFunction
  [key: string]: any
}

export default function UserTableRow({
  row,
  selected,
  refetch,
  onDeleteRow,
}: Props) {
  const { name, role, document, email, active, createdAt, allowedPayments } = row

  const confirm = useBoolean()
  const quickEdit = useBoolean()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const roleConfig = ROLE_CONFIG[role] || ROLE_CONFIG.user

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell>
          <Typography variant='body2' fontWeight={600} noWrap>
            {name}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant='body2' color='text.secondary' noWrap sx={{ fontSize: '0.8rem' }}>
            {email}
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
            {document ? masks.document(document) : '—'}
          </Typography>
        </TableCell>

        <TableCell>
          <Stack direction='row' spacing={0.5} flexWrap='wrap'>
            {allowedPayments && PAYMENT_ICONS.map((pm) => {
              const enabled = allowedPayments[pm.key]
              if (!enabled) return null
              return (
                <Tooltip key={pm.key} title={pm.label} arrow>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${pm.color}14`,
                    }}
                  >
                    <Iconify icon={pm.icon} width={14} sx={{ color: pm.color }} />
                  </Box>
                </Tooltip>
              )
            })}
            {!allowedPayments && <Typography variant='caption' color='text.disabled'>—</Typography>}
          </Stack>
        </TableCell>

        <TableCell>
          {role !== 'user' ? (
            <Chip
              label={roleConfig.label}
              size='small'
              color={roleConfig.color}
              variant='soft'
              sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
            />
          ) : (
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8rem' }}>
              Vendedor
            </Typography>
          )}
        </TableCell>

        <TableCell>
          <Label
            variant='soft'
            color={active ? 'success' : 'warning'}
            sx={{ fontSize: '0.7rem' }}
          >
            {active ? 'Ativo' : 'Inativo'}
          </Label>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8rem' }}>
            {format(new Date(createdAt), 'dd/MM/yy')}
          </Typography>
        </TableCell>

        <TableCell align='center' sx={{ px: 0.5 }}>
          <IconButton size='small' onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <Iconify icon='solar:menu-dots-bold' width={18} />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 160, boxShadow: 3 } } }}
          >
            <MenuItem onClick={() => { setMenuAnchor(null); quickEdit.onTrue() }}>
              <ListItemIcon>
                <Iconify icon='solar:pen-bold' width={18} />
              </ListItemIcon>
              <ListItemText primary='Editar' primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 0.5 }} />
            <MenuItem onClick={() => { setMenuAnchor(null); confirm.onTrue() }} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <Iconify icon='solar:trash-bin-minimalistic-bold' width={18} sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText primary='Deletar' primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>

      <UserCreateEditForm
        currentUser={row}
        onRefleshTable={() => refetch()}
        open={quickEdit.value}
        onClose={quickEdit.onFalse}
      />

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title='Deletar Vendedor'
        content={
          <Typography variant='body2'>
            Tem certeza que deseja deletar <strong>{name}</strong>? Esta ação não pode ser desfeita.
          </Typography>
        }
        action={
          <Button variant='contained' color='error' onClick={onDeleteRow}>
            Deletar
          </Button>
        }
      />
    </>
  )
}
